import colors from 'tailwindcss/colors';

import {
    CAPACITY_STATS_COUNT_KEYS,
    CombinedStatisticsKey,
    VisitorType,
    VisitStatisticTotalsKey,
} from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PAYMENT_ITEM_TYPE, PAYMENT_TYPE } from 'src/features/payment/utils/types';
import { eventMessages } from 'src/features/eventLog/utils/messages';
import { visitorMessages } from 'src/features/visitor/utils/messages';

export const statisticsTypeMapping: Record<CombinedStatisticsKey, { id: string; defaultMessage: string }> = {
    [VisitStatisticTotalsKey.CLIENT_VISITS_COUNT]: statisticMessages.clients,
    [VisitStatisticTotalsKey.MEMBER_VISITS_COUNT]: statisticMessages.members,
    [VisitStatisticTotalsKey.RESERVATION_VISITS_COUNT]: statisticMessages.reservations,
    [VisitStatisticTotalsKey.SUBSCRIPTION_VISITS_COUNT]: statisticMessages.subscriptions,
    [CAPACITY_STATS_COUNT_KEYS.FREE_CLIENT_SPACES_COUNT]: statisticMessages.freeClientSpaces,
    [CAPACITY_STATS_COUNT_KEYS.FREE_MEMBER_SPACES_COUNT]: statisticMessages.freeMemberSpaces,
    [CAPACITY_STATS_COUNT_KEYS.FREE_RESERVATION_SPACES_COUNT]: statisticMessages.freeReservationSpaces,
    [CAPACITY_STATS_COUNT_KEYS.FREE_SUBSCRIPTION_SPACES_COUNT]: statisticMessages.freeSubscriptionSpaces,
    [CAPACITY_STATS_COUNT_KEYS.FREE_SPACES_COUNT]: statisticMessages.freeSpacesCount,
    [CAPACITY_STATS_COUNT_KEYS.TOTAL_FREE_SPACES_COUNT]: statisticMessages.totalFreeSpacesCount,
    [PAYMENT_TYPE.CARD]: paymentsMessages.card,
    [PAYMENT_TYPE.CASH]: paymentsMessages.cash,
    [PAYMENT_TYPE.ONLINE]: paymentsMessages.online,
    [PAYMENT_TYPE.BANK]: paymentsMessages.bank,
    ['card_payments_count']: paymentsMessages.card,
    ['cash_payments_count']: paymentsMessages.cash,
    ['online_payments_count']: paymentsMessages.online,
    ['bank_payments_count']: paymentsMessages.bank,
    ['client']: statisticMessages.clients,
    ['member']: statisticMessages.members,
    ['reservation']: statisticMessages.reservations,
    ['subscription']: statisticMessages.subscriptions,
    ['gateway_pass_allowed']: eventMessages.gatewayPassAllowed,
    ['gateway_pass_not_allowed']: eventMessages.gatewayPassNotAllowed,
    [PAYMENT_ITEM_TYPE.ACTIVE_STAY]: visitorMessages.activeStayFull,
    [PAYMENT_ITEM_TYPE.PAST_STAY]: visitorMessages.pastStay,
    [PAYMENT_ITEM_TYPE.MEMBERSHIP]: statisticMessages.subscriptions,
};

export const colorCategoryMapping: Record<VisitStatisticTotalsKey, string> = {
    [VisitStatisticTotalsKey.CLIENT_VISITS_COUNT]: colors.green[300],
    [VisitStatisticTotalsKey.MEMBER_VISITS_COUNT]: colors.blue[300],
    [VisitStatisticTotalsKey.RESERVATION_VISITS_COUNT]: colors.purple[300],
    [VisitStatisticTotalsKey.SUBSCRIPTION_VISITS_COUNT]: colors.orange[300],
};

export const colorPaymentMethodMapping: Record<PAYMENT_TYPE, string> = {
    [PAYMENT_TYPE.CARD]: colors.blue[300],
    [PAYMENT_TYPE.CASH]: colors.green[300],
    [PAYMENT_TYPE.ONLINE]: colors.purple[300],
    [PAYMENT_TYPE.BANK]: colors.orange[300],
};

export const colorMonthlyPaymentMapping: Record<string, string> = {
    ['card_payments_count']: colors.blue[300],
    ['cash_payments_count']: colors.green[300],
    ['online_payments_count']: colors.purple[300],
    ['bank_payments_count']: colors.orange[300],
};

export const colorGatewayStatisticsMapping: Record<string, string> = {
    ['gateway_pass_allowed']: colors.green[300],
    ['gateway_pass_not_allowed']: colors.red[300],
};

export const capacityHourlyBreakdownColorMapping: Record<VisitorType, string> = {
    [VisitorType.CLIENT]: colors.green[300],
    [VisitorType.MEMBER]: colors.blue[300],
    [VisitorType.RESERVATION]: colors.purple[300],
    [VisitorType.SUBSCRIPTION]: colors.orange[300],
};

export const colorPaymentByProductMapping: Record<PAYMENT_ITEM_TYPE | string, string> = {
    [PAYMENT_ITEM_TYPE.ACTIVE_STAY]: colors.green[300],
    [PAYMENT_ITEM_TYPE.PAST_STAY]: colors.red[300],
    [PAYMENT_ITEM_TYPE.SUBSCRIPTION]: colors.orange[300],
    [PAYMENT_ITEM_TYPE.RESERVATION]: colors.purple[300],
};

export const allColorCategoryMapping: Record<CombinedStatisticsKey, string> = {
    [VisitStatisticTotalsKey.CLIENT_VISITS_COUNT]: colors.green[300],
    [VisitStatisticTotalsKey.MEMBER_VISITS_COUNT]: colors.blue[300],
    [VisitStatisticTotalsKey.RESERVATION_VISITS_COUNT]: colors.purple[300],
    [VisitStatisticTotalsKey.SUBSCRIPTION_VISITS_COUNT]: colors.orange[300],
    [CAPACITY_STATS_COUNT_KEYS.FREE_CLIENT_SPACES_COUNT]: colors.green[300],
    [CAPACITY_STATS_COUNT_KEYS.FREE_MEMBER_SPACES_COUNT]: colors.blue[300],
    [CAPACITY_STATS_COUNT_KEYS.FREE_RESERVATION_SPACES_COUNT]: colors.purple[300],
    [CAPACITY_STATS_COUNT_KEYS.FREE_SUBSCRIPTION_SPACES_COUNT]: colors.orange[300],
    [CAPACITY_STATS_COUNT_KEYS.FREE_SPACES_COUNT]: colors.cyan[300],
    [CAPACITY_STATS_COUNT_KEYS.TOTAL_FREE_SPACES_COUNT]: colors.pink[300],
    [PAYMENT_TYPE.CARD]: colors.blue[300],
    [PAYMENT_TYPE.CASH]: colors.green[300],
    [PAYMENT_TYPE.ONLINE]: colors.purple[300],
    [PAYMENT_TYPE.BANK]: colors.orange[300],
    [VisitorType.CLIENT]: colors.green[300],
    [VisitorType.MEMBER]: colors.blue[300],
    [VisitorType.RESERVATION]: colors.purple[300],
    [VisitorType.SUBSCRIPTION]: colors.orange[300],
    ['gateway_pass_allowed']: colors.green[300],
    ['gateway_pass_not_allowed']: colors.red[300],
    ['card_payments_count']: colors.blue[300],
    ['cash_payments_count']: colors.green[300],
    ['online_payments_count']: colors.purple[300],
    ['bank_payments_count']: colors.orange[300],
    [PAYMENT_ITEM_TYPE.ACTIVE_STAY]: colors.green[300],
    [PAYMENT_ITEM_TYPE.PAST_STAY]: colors.red[300],
    [PAYMENT_ITEM_TYPE.MEMBERSHIP]: colors.orange[300],
};

export const RECHARTS_COLOR_FILLS = [
    colors.green[300],
    colors.blue[300],
    colors.purple[300],
    colors.orange[300],
    colors.cyan[300],
    colors.pink[300],
    colors.teal[300],
    colors.red[300],
];
